<template>
  <Card
    class="product-card"
    v-bind="$attrs"
    :theme="cardTheme"
    :class="[
      'product-card--' + theme,
      {
        'product-card--disabled': disabled,
        'product-card--selected': selected,
      },
    ]"
  >
    <template #header>
      <ImageComponent v-if="image" :src="image" :preview="preview" :alt="value.name" size="contain" />
      <FontAwesomeIcon v-if="!image" :icon="['fal', 'shopping-cart']" size="4x" style="opacity: 0.4" />
      <div v-if="selected" class="product-card__selection">
        {{ $t('selected') }}
        <FontAwesomeIcon :icon="['fas', 'check-circle']" />
      </div>
      <div class="product-card__badges">
        <slot name="badges" />
        <Badge v-if="value.conditions" theme="warning " class="product-card__scoped"
          >{{ $t('conditionsMessage') }} ({{ value.conditions }})</Badge
        >
        <Badge v-if="value.bundleSize" theme="primary" class="product-card__bundle">{{ $t('productBundle') }}</Badge>
        <Badge
          v-if="value.isScoped"
          theme="warning"
          icon="fa-pro-light:link-slash"
          class="product-card__scoped"
          v-tooltip="$t(`scopedProductExplanation`)"
          >{{ $t('scopedProduct') }}</Badge
        >
      </div>
    </template>
    <template #default>
      <div class="product-card__title">
        <slot name="title">
          <span data-field="name">{{ value.name }}</span>
          <Badge v-if="value.demo" theme="warning" class="product-card__demo">{{ $t('demo') }}</Badge>
          <span v-if="value.internalId" data-field="internalId">{{ value.internalId }}</span>
        </slot>
      </div>
      <div class="product-card__subtitle">
        <slot name="subtitle">
          <template v-if="value.pricing && value.pricing.type !== 'request'">
            {{ price | Currency }}
            <template v-if="value.pricing && value.pricing.type === 'area'"> / m²</template>
            {{ $t('includingVat') }}
          </template>
          <template v-else>{{ $t('priceOnRequest') }}</template>
        </slot>
      </div>
    </template>
  </Card>
</template>

<script>
import Card from '@/components/Card'
import Badge from '@/components/Badge'
import ImageComponent from '@/components/Image'
import { Currency } from '@/filters'
import { getGross } from './utils'
import { thumbhashToBase64 } from '@/utils/thumbhash'

export default {
  props: {
    value: Object,
    disabled: Boolean,
    selected: Boolean,
    theme: {
      type: String,
      enum: ['card', 'list', 'shop'],
      default: 'card',
    },
  },
  computed: {
    image() {
      return this.$path('value.thumbnail.url') || this.$path('value.images.0.url')
    },
    preview() {
      const image = this.$path('value.thumbnail') || this.$path('value.images.0')
      if (!image) return
      return thumbhashToBase64(image)
    },
    price() {
      if (this.value.pricing.type === 'request') return
      return getGross(this.value.pricing, { round: false })
    },
    cardTheme() {
      switch (this.theme) {
        default:
          return 'default'
        case 'shop':
          return 'border'
      }
    },
  },
  filters: {
    Currency,
  },
  components: {
    ImageComponent,
    Card,
    Badge,
  },
}
</script>

<style lang="scss">
.product-card {
  position: relative;

  &--disabled {
    text-decoration: line-through;
    pointer-events: none;
    opacity: 0.4;
  }

  &__demo {
    font-weight: normal;
  }

  &__selection {
    position: absolute;
    top: 1rem;
    left: 1rem;
    padding: 0.4rem 0.8rem;
    background-color: css-darken('primary', 15%);
    border: 1px solid css-darken('primary', 5%);
    font-size: var(--font-size-small);
    color: white;
    border-radius: var(--border-radius);

    svg {
      margin-left: 0.25rem;
      opacity: 0.5;
    }
  }

  &.product-card--list {
    text-align: left;
    flex-direction: row;
    align-items: center;

    box-shadow: none;
    background-color: transparent;

    .card__header,
    .card__content {
      padding: 0;
    }

    .card__content {
      border-top: none;
      background-color: transparent;
    }

    .product-card__badges {
      display: none;
    }

    .card__header {
      flex-grow: 0;
      margin-right: 0.75rem;

      $size: 3rem;
      width: $size;
      height: $size;
      min-height: initial;
      border-radius: var(--border-radius);
    }

    .product-card__thumbnail {
      border-width: 0;
    }
  }

  .card__header {
    display: flex;
    justify-content: center;
    align-items: center;
    border-bottom: none;
    padding: 0;
    background-color: white;
    position: relative;

    img {
      object-fit: contain;
      position: absolute;
      width: 100%;
      height: 100%;
    }
  }

  &__badges {
    $space: 1rem;
    position: absolute;
    bottom: $space;
    right: $space;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  &__title {
    display: flex;
    align-items: baseline;
    line-height: 1.3;
    gap: 0.5rem;

    [data-field='internalId'] {
      font-weight: normal;
      opacity: 0.5;
      font-size: 0.8em;
    }
  }

  &__subtitle {
    font-size: 0.8em;
    margin-top: 0.5rem;
    opacity: var(--opacity-muted);
  }

  .card__content {
    border-top: 1px solid var(--border-color);
    border-radius: 0 0 var(--border-radius-md) var(--border-radius-md);
    padding: 1.5rem;
  }
}
</style>
